const endpoints = {
  solicitar: "atendimentos/access/salvarSolicitarRevisao",
  liberar: "atendimentos/access/liberarLaudoPaciente",
  pdf: "atendimentos/access/associaPDFLaudoLiberado"
};
export async function enviarOperacaoLaudo(api, jwt, { tipo, dados }) {
  if(!Object.prototype.hasOwnProperty.call(endpoints, tipo) || !jwt.getToken()) {
    throw Object.assign(new Error("Sessão ou operação indisponível. Nenhuma solicitação foi enviada."), { antesDoEnvio: true });
  }
  try { api.setHeader(); } catch(error) { error.antesDoEnvio = true; throw error; }
  let data;
  try { ({ data } = await api.postOnce(endpoints[tipo], dados, { timeout: 120000 })); }
  catch(error) {
    if(error.response?.data?.RESULTADO === false && error.response.data.ESTADO === "FALHA") {
      throw Object.assign(new Error(error.response.data.MENSAGEM || "A operação não foi concluída."), { rejeitada: true });
    }
    throw error;
  }
  if(data?.RESULTADO === false && data.ESTADO === "FALHA") {
    throw Object.assign(new Error(data.MENSAGEM || "A operação não foi concluída."), { rejeitada: true });
  }
  // A confirmação vem de uma leitura posterior, inclusive para respostas legadas.
  return data;
}

const controladores = new WeakMap();
const identidade = vm => JSON.stringify([vm.$route.fullPath, vm.paciente.ID_PACIENTE, vm.agendaExame.ID_AGENDA_EXAME, vm.textoIncluindo.ID_DIVERSOS || vm.textoIncluindo.ID]);
const mesmoId = (a, b) => a !== undefined && b !== undefined && String(a) === String(b);
export function estadoLaudoConfirmado(req, atual) {
  if(!atual || typeof atual !== "object" || Array.isArray(atual)) { return false; }
  if(req.tipo === "solicitar") {
    return Object.prototype.hasOwnProperty.call(atual, "ID_MEDICO_REVISOR_INDICADO") && mesmoId(atual.ID_DIVERSOS, req.dados.ID_DIVERSOS) &&
      String(atual.ID_MEDICO_REVISOR_INDICADO ?? 0) === String(req.dados.ID_MEDICO_REVISOR_INDICADO);
  }
  if(!atual.ID_PACIENTE_LAUDO || !mesmoId(atual.ID_DIVERSOS, req.dados.ID_DIVERSOS)) { return false; }
  if(req.tipo === "liberar") {
    return atual.DESATUALIZADO === false && String(atual.APENAS_CONTATO_EXTERNO ?? 0) === (String(req.dados.OPCAO_LIBERAR) === "1" ? "1" : "0") &&
      (String(req.dados.OPCAO_LIBERAR) !== "2" || !atual.CONTATO_CRM);
  }
  const caminhoEsperado = req.resposta?.RESULTADO === true ? req.resposta.CAMINHO_PDF : null;
  return mesmoId(atual.ID_PACIENTE_LAUDO, req.dados.ID_PACIENTE_LAUDO) && atual.PDF_DISPONIVEL === true &&
    String(atual.TEM_PDF) === "1" && atual.PDF_DESATUALIZADO === false && !!atual.CAMINHO_PDF &&
    (caminhoEsperado ? atual.CAMINHO_PDF === caminhoEsperado : atual.CAMINHO_PDF !== req.anterior.CAMINHO_PDF) &&
    String(atual.PDF_ASSINAR ?? 0) === (req.dados.ASSINAR ? "1" : "0");
}

export function operacoesLaudo(vm, capturar) {
  if(controladores.has(vm)) { return controladores.get(vm); }
  const pendentes = new Map();
  let corrente = null;
  const estado = (mensagem = "", consultar = false, ocupada = false) => { vm.operacaoLaudo = { mensagem, consultar, ocupada }; };
  function sincronizar() {
    corrente = null;
    estado(pendentes.has(identidade(vm)) ? "Há uma operação aguardando confirmação para este laudo. Consulte o resultado antes de continuar." : "", pendentes.has(identidade(vm)));
  }
  async function conferir(req, vigente) {
    const dados = req.tipo === "solicitar" ? { ID_DIVERSOS: req.dados.ID_DIVERSOS } : {
      ID_PACIENTE: req.dados.ID_PACIENTE, ID_AGENDA_EXAME: req.dados.ID_AGENDA_EXAME || "", ID_DIVERSOS: req.dados.ID_DIVERSOS
    };
    const atual = await vm.consultarLeituraEditorTexto({ recurso: req.tipo === "solicitar" ? "diverso" : "pacienteLaudo", dados });
    if(!vigente()) { return false; }
    if(!estadoLaudoConfirmado(req, atual)) { return false; }
    pendentes.delete(req.chave);
    estado();
    // Retira a pendência antes dos efeitos de interface: falha visual não permite reenviar a escrita.
    req.confirmada = true;
    await req.aplicar(atual, vigente);
    return true;
  }
  function executar(tipo, preparar, aplicar) {
    if(!vm.editorTextoPronto) { return Promise.resolve(false); }
    const chave = identidade(vm);
    if(corrente) { return corrente.chave === chave && corrente.tipo === tipo ? corrente.promise : Promise.resolve(false); }
    if(pendentes.has(chave)) { sincronizar(); return Promise.resolve(false); }
    if(vm.isLoading || vm.leiturasTexto.conteudo) { return Promise.resolve(false); }
    const token = { chave, tipo }, contextoVigente = capturar();
    const vigente = () => corrente === token && contextoVigente();
    corrente = token;
    estado("", false, true);
    vm.isLoading = true;
    token.promise = Promise.resolve().then(async () => {
      let req;
      try {
        const preparado = await preparar(vigente);
        if(!preparado || !vigente()) { return false; }
        req = { ...preparado, dados: JSON.parse(JSON.stringify(preparado.dados)), tipo, chave, aplicar };
        pendentes.set(chave, req);
        try { req.resposta = await vm.enviarOperacaoLaudo({ tipo, dados: req.dados }); }
        catch(error) {
          if(error.antesDoEnvio || error.rejeitada) { pendentes.delete(chave); throw error; }
        }
        if(!vigente()) { return false; }
        if(await conferir(req, vigente)) { return true; }
        if(vigente()) { estado("Não foi possível confirmar o resultado. O envio não será repetido; consulte o estado do laudo.", true); }
        return false;
      } catch(error) {
        if(vigente()) {
          estado(req?.confirmada ? "Operação confirmada, mas não foi possível atualizar a tela. Reabra o laudo para conferir." :
            pendentes.has(chave) ? "Não foi possível consultar o resultado. O envio não será repetido." : error.message || "Não foi possível concluir a operação.", pendentes.has(chave));
        }
        return false;
      } finally {
        if(vigente()) { vm.isLoading = false; vm.operacaoLaudo.ocupada = false; corrente = null; }
      }
    });
    return token.promise;
  }
  function consultar() {
    if(corrente) { return corrente.promise; }
    const chave = identidade(vm), req = pendentes.get(chave);
    if(!req || !vm.editorTextoPronto) { return Promise.resolve(false); }
    const token = { chave, tipo: req.tipo }, contextoVigente = capturar();
    const vigente = () => corrente === token && contextoVigente();
    corrente = token; estado("Consultando o resultado…", false, true); vm.isLoading = true;
    token.promise = Promise.resolve().then(async () => {
      try {
        const ok = await conferir(req, vigente);
        if(!ok && vigente()) { estado("O resultado ainda não foi confirmado. Confira o laudo; nenhuma nova gravação foi enviada.", true); }
        return ok;
      } catch(error) {
        if(vigente()) { estado(req.confirmada ? "Operação confirmada. Reabra o laudo para atualizar a tela." : "Consulta indisponível. Tente consultar novamente.", !req.confirmada); }
        return false;
      } finally {
        if(vigente()) { vm.isLoading = false; vm.operacaoLaudo.ocupada = false; corrente = null; }
      }
    });
    return token.promise;
  }
  const controlador = { executar, consultar, sincronizar, pendente: () => pendentes.has(identidade(vm)), invalidar: () => { corrente = null; } };
  controladores.set(vm, controlador);
  return controlador;
}
